import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { toast } from "@/lib/notify";
import {
  Plus, Upload, Trash2, DollarSign, TrendingUp,
  FileSpreadsheet, Download, ChevronDown, Pencil,
} from "lucide-react";
import { useSortableTable } from "@/hooks/useSortableTable";
import { SortableHead } from "@/components/SortableHead";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Pagination, paginate } from "@/components/Pagination";

/**
 * Inline panel rendered inside the bulk-upload dialogs when the backend
 * returns a partial-success result (some rows accepted, some rejected).
 * Shows every per-row reason verbatim from the API so operators can fix
 * the CSV without digging through DevTools → Network.
 */
function BulkUploadResultPanel({ result, testIdPrefix }) {
  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(result.errors.join("\n"));
      toast.success("Errors copied to clipboard");
    } catch { toast.error("Could not copy to clipboard"); }
  };
  return (
    <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 space-y-2" data-testid={`${testIdPrefix}-result-panel`}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium text-amber-300">
          <span className="text-emerald-400">{result.created} row{result.created === 1 ? "" : "s"} accepted</span>
          {typeof result.updated === "number" && (
            <>, <span className="text-emerald-400">{result.updated} updated</span></>
          )}
          {" · "}
          <span className="text-amber-300">{result.errors.length} row{result.errors.length === 1 ? "" : "s"} rejected</span>
        </p>
        <Button variant="ghost" size="sm" className="h-7 text-[11px]" onClick={copyAll} data-testid={`${testIdPrefix}-copy-errors`}>
          Copy errors
        </Button>
      </div>
      <div className="max-h-44 overflow-y-auto rounded border border-amber-500/20 bg-card/40 divide-y divide-amber-500/10" data-testid={`${testIdPrefix}-errors-list`}>
        {result.errors.map((msg, i) => (
          <div key={i} className="px-2.5 py-1.5 text-[11px] font-mono text-amber-100/90 leading-snug">
            {msg}
          </div>
        ))}
      </div>
      <p className="text-[10px] text-muted-foreground leading-snug">
        Fix the rejected rows in your CSV (most common: investor name doesn&apos;t match,
        or investor onboarding isn&apos;t complete), then click Re-upload.
      </p>
    </div>
  );
}

export default function TransactionsPage() {
  const { user } = useAuth();
  const { fundId: portalFundId } = useParams();
  const [funds, setFunds] = useState([]);
  const [selectedFund, setSelectedFund] = useState(null);
  const [investors, setInvestors] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [capitalTxns, setCapitalTxns] = useState([]);
  const [perfTxns, setPerfTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [txnLoading, setTxnLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("capital");

  const [capitalDialog, setCapitalDialog] = useState(false);
  const [perfDialog, setPerfDialog] = useState(false);
  const [bulkCapitalDialog, setBulkCapitalDialog] = useState(false);
  const [bulkPerfDialog, setBulkPerfDialog] = useState(false);

  const [capForm, setCapForm] = useState({ investor_id: "", date: "", subscription_amount: "", redemption_amount: "", shares: "", class_description: "", notes: "", bank_account_code: "", bank_id: "", dealing_date: "" });
  // Banks dropdown — fetched live from GL via /api/funds/{id}/banks
  // every time a new fund is selected on the capital-txn form.
  const [banks, setBanks] = useState([]);
  const [banksLoading, setBanksLoading] = useState(false);
  const [banksError, setBanksError] = useState("");
  const [perfForm, setPerfForm] = useState({ investor_id: "", period_id: "", total_income: "", management_fee: "", incentive_fee: "", class_description: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkPeriod, setBulkPeriod] = useState("");
  // Result of the last bulk upload — kept so per-row error reasons can be
  // shown inside the dialog instead of being collapsed into a toast count.
  // Shape: { created: number, updated?: number, errors: string[] }
  const [bulkResult, setBulkResult] = useState(null);
  const [confirmDeleteTxn, setConfirmDeleteTxn] = useState(null);
  const [filterInvestor, setFilterInvestor] = useState("all");
  const [filterPeriod, setFilterPeriod] = useState("all");
  const [nextPeriodLoading, setNextPeriodLoading] = useState(false);
  const [nextPeriodLabel, setNextPeriodLabel] = useState("");
  const bulkCapRef = useRef(null);
  const bulkPerfRef = useRef(null);

  // Edit state
  const [editCapDialog, setEditCapDialog] = useState(false);
  const [editPerfDialog, setEditPerfDialog] = useState(false);
  const [editCapForm, setEditCapForm] = useState(null);
  const [editPerfForm, setEditPerfForm] = useState(null);

  const canManage = user?.role === "super_admin";

  const loadFunds = useCallback(async () => {
    try {
      if (portalFundId) {
        const { data } = await API.get(`/funds/${portalFundId}`);
        setFunds([data]);
        setSelectedFund(data);
      } else if (user?.role === "admin") {
        const fundIds = user?.fund_ids || [];
        if (fundIds.length > 0) {
          const results = await Promise.all(fundIds.map(id => API.get(`/funds/${id}`).catch(() => null)));
          const loaded = results.filter(Boolean).map(r => r.data);
          setFunds(loaded);
          if (loaded.length > 0) setSelectedFund(loaded.find(f => f.status === "active") || loaded[0]);
        }
      } else {
        const { data } = await API.get("/funds");
        setFunds(data);
        if (data.length > 0) setSelectedFund(data.find(f => f.status === "active") || data[0]);
      }
    } catch { toast.error("Failed to load funds"); }
    finally { setLoading(false); }
  }, [user, portalFundId]);

  useEffect(() => { loadFunds(); }, [loadFunds]);

  const loadFundData = useCallback(async () => {
    if (!selectedFund) return;
    setTxnLoading(true);
    try {
      const [invRes, periodRes, capRes, perfRes] = await Promise.all([
        API.get(`/investors?fund_id=${selectedFund.id}`),
        API.get(`/funds/${selectedFund.id}/reporting-periods`),
        API.get(`/funds/${selectedFund.id}/capital-transactions`),
        API.get(`/funds/${selectedFund.id}/performance-transactions`),
      ]);
      setInvestors(invRes.data);
      setPeriods(periodRes.data);
      setCapitalTxns(capRes.data);
      setPerfTxns(perfRes.data);
    } catch { toast.error("Failed to load transactions"); }
    finally { setTxnLoading(false); }
  }, [selectedFund]);

  useEffect(() => { loadFundData(); }, [loadFundData]);

  // Pull GL bank list whenever the fund changes. The dropdown is rendered
  // even if this call fails — we just show the error string so the
  // operator can act on it (e.g. retry once GL is reachable).
  useEffect(() => {
    if (!selectedFund) { setBanks([]); setBanksError(""); return; }
    let cancelled = false;
    setBanksLoading(true); setBanksError("");
    API.get(`/funds/${selectedFund.id}/banks`)
      .then((res) => {
        if (cancelled) return;
        setBanks(Array.isArray(res.data?.banks) ? res.data.banks : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setBanks([]);
        const detail = err?.response?.data?.detail || "";
        setBanksError(/auth failed/i.test(detail) ? "Unable to load banks" : (detail || "Unable to load banks"));
      })
      .finally(() => { if (!cancelled) setBanksLoading(false); });
    return () => { cancelled = true; };
  }, [selectedFund]);

  // Auto-compute next period when investor or offering changes
  useEffect(() => {
    if (!perfForm.investor_id || !selectedFund) {
      setPerfForm(prev => ({ ...prev, period_id: "" }));
      setNextPeriodLabel("");
      return;
    }
    const fetchNext = async () => {
      setNextPeriodLoading(true);
      try {
        const params = new URLSearchParams({ investor_id: perfForm.investor_id, class_description: perfForm.class_description || "" });
        const { data } = await API.get(`/funds/${selectedFund.id}/next-period?${params}`);
        if (data.next_period_id) {
          setPerfForm(prev => ({ ...prev, period_id: data.next_period_id }));
          setNextPeriodLabel(data.next_period_label || data.next_period_id);
        } else {
          setPerfForm(prev => ({ ...prev, period_id: "" }));
          setNextPeriodLabel(data.message || "All periods covered");
        }
      } catch {
        setPerfForm(prev => ({ ...prev, period_id: "" }));
        setNextPeriodLabel("");
      }
      finally { setNextPeriodLoading(false); }
    };
    fetchNext();
  }, [perfForm.investor_id, perfForm.class_description, selectedFund]);

  const createCapitalTxn = async () => {
    if (!capForm.investor_id || !capForm.date) { toast.error("Investor and date required"); return; }
    if (!capForm.subscription_amount && !capForm.redemption_amount) { toast.error("Enter subscription or redemption amount"); return; }
    if (!capForm.bank_account_code) { toast.error("Please select a bank"); return; }
    setSubmitting(true);
    try {
      await API.post(`/funds/${selectedFund.id}/capital-transactions`, {
        ...capForm, subscription_amount: parseFloat(capForm.subscription_amount) || 0, redemption_amount: parseFloat(capForm.redemption_amount) || 0, shares: parseFloat(capForm.shares) || 0,
      });
      toast.success("Capital transaction created");
      setCapitalDialog(false);
      setCapForm({ investor_id: "", date: "", subscription_amount: "", redemption_amount: "", shares: "", class_description: "", notes: "", bank_account_code: "", bank_id: "", dealing_date: "" });
      loadFundData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  };

  const deleteCapitalTxn = async (txnId) => {
    try { await API.delete(`/funds/${selectedFund.id}/capital-transactions/${txnId}`); toast.success("Deleted"); loadFundData(); } catch (err) { toast.error(err.response?.data?.detail || "Failed to delete transaction"); }
  };

  const createPerfTxn = async () => {
    if (!perfForm.investor_id || !perfForm.period_id) { toast.error("Investor and period required"); return; }
    setSubmitting(true);
    try {
      await API.post(`/funds/${selectedFund.id}/performance-transactions`, {
        ...perfForm, total_income: parseFloat(perfForm.total_income) || 0, management_fee: parseFloat(perfForm.management_fee) || 0, incentive_fee: parseFloat(perfForm.incentive_fee) || 0,
      });
      toast.success("Performance entry saved");
      setPerfDialog(false);
      setPerfForm({ investor_id: "", period_id: "", total_income: "", management_fee: "", incentive_fee: "", class_description: "", notes: "" });
      loadFundData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  };

  const deletePerfTxn = async (txnId) => {
    try { await API.delete(`/funds/${selectedFund.id}/performance-transactions/${txnId}`); toast.success("Deleted"); loadFundData(); } catch (err) { toast.error(err.response?.data?.detail || "Failed to delete transaction"); }
  };

  const editCapitalTxn = async () => {
    if (!editCapForm) return;
    setSubmitting(true);
    try {
      await API.put(`/funds/${selectedFund.id}/capital-transactions/${editCapForm.id}`, {
        investor_id: editCapForm.investor_id,
        date: editCapForm.date,
        subscription_amount: parseFloat(editCapForm.subscription_amount) || 0,
        redemption_amount: parseFloat(editCapForm.redemption_amount) || 0,
        shares: parseFloat(editCapForm.shares) || 0,
        class_description: editCapForm.class_description || "",
        notes: editCapForm.notes || "",
        dealing_date: editCapForm.dealing_date || "",
      });
      toast.success("Transaction updated");
      setEditCapDialog(false);
      setEditCapForm(null);
      loadFundData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to update"); }
    finally { setSubmitting(false); }
  };

  const editPerfTxn = async () => {
    if (!editPerfForm) return;
    setSubmitting(true);
    try {
      await API.put(`/funds/${selectedFund.id}/performance-transactions/${editPerfForm.id}`, {
        investor_id: editPerfForm.investor_id,
        period_id: editPerfForm.period_id,
        total_income: parseFloat(editPerfForm.total_income) || 0,
        management_fee: parseFloat(editPerfForm.management_fee) || 0,
        incentive_fee: parseFloat(editPerfForm.incentive_fee) || 0,
        class_description: editPerfForm.class_description || "",
        notes: editPerfForm.notes || "",
      });
      toast.success("Entry updated");
      setEditPerfDialog(false);
      setEditPerfForm(null);
      loadFundData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to update"); }
    finally { setSubmitting(false); }
  };

  const handleBulkCapitalUpload = async () => {
    if (!bulkFile) { toast.error("Select a CSV file"); return; }
    setSubmitting(true);
    setBulkResult(null);
    try {
      const fd = new FormData(); fd.append("file", bulkFile);
      const { data } = await API.post(`/funds/${selectedFund.id}/capital-transactions/bulk`, fd);
      const errs = data.errors || [];
      if (errs.length === 0) {
        // Pure success path — preserved exactly as before.
        toast.success(`${data.created} transactions created`);
        setBulkCapitalDialog(false); setBulkFile(null); setBulkResult(null);
        loadFundData();
      } else {
        // Partial success — keep the dialog open so the operator can read
        // every row-level reason, fix the CSV, and re-upload without
        // having to dig through the Network tab.
        toast.warning(`${data.created} created, ${errs.length} rows skipped — see details`);
        setBulkResult({ created: data.created || 0, errors: errs });
        loadFundData();  // refresh whatever did succeed
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setSubmitting(false); }
  };

  const handleBulkPerfUpload = async () => {
    if (!bulkFile || !bulkPeriod) { toast.error("Select file and period"); return; }
    setSubmitting(true);
    setBulkResult(null);
    try {
      const fd = new FormData(); fd.append("file", bulkFile);
      const { data } = await API.post(`/funds/${selectedFund.id}/performance-transactions/bulk?period_id=${bulkPeriod}`, fd);
      const errs = data.errors || [];
      if (errs.length === 0) {
        toast.success(`${data.created} created, ${data.updated} updated`);
        setBulkPerfDialog(false); setBulkFile(null); setBulkPeriod(""); setBulkResult(null);
        loadFundData();
      } else {
        toast.warning(`${data.created} created, ${data.updated || 0} updated, ${errs.length} rows skipped — see details`);
        setBulkResult({ created: data.created || 0, updated: data.updated || 0, errors: errs });
        loadFundData();
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setSubmitting(false); }
  };

  const downloadTemplate = async (type) => {
    if (type === "capital") {
      // Capital template is .xlsx so the Bank column can carry a real Excel
      // data-validation dropdown listing exactly the bank account codes
      // available for this fund (same source as the manual modal). Performance
      // template stays CSV — it has no Bank column to gate.
      if (!selectedFund) { toast.error("Select a fund first"); return; }
      try {
        const ExcelJS = (await import("exceljs")).default;
        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet("Capital Transactions");
        const headers = ["Date", "Investor Name", "Subscription Amount", "Redemption Amount", "Shares", "Class", "Bank", "Notes"];
        ws.addRow(headers);
        ws.getRow(1).font = { bold: true };
        ws.columns = [
          { width: 14 }, { width: 26 }, { width: 22 }, { width: 22 },
          { width: 10 }, { width: 14 }, { width: 28 }, { width: 30 },
        ];
        // Example row — use a real bank code if any exist so operators see
        // exactly what the dropdown produces.
        const sampleBank = banks[0]?.bank_account_code || "";
        ws.addRow(["2026-01-15", "John Doe", 100000, 0, 1000, "Class A", sampleBank, "Initial subscription"]);
        // Excel data-validation dropdown on the Bank column (column G,
        // rows 2..1000). Source is the comma-joined list of bank_account_codes
        // — same values the manual modal stores in capForm.bank_account_code.
        const bankCodes = banks.map(b => b.bank_account_code).filter(Boolean);
        if (bankCodes.length > 0) {
          // Excel caps inline list length at 255 chars. For longer lists we
          // park codes in a hidden sheet and reference the range instead.
          const inline = bankCodes.join(",");
          if (inline.length <= 250) {
            for (let r = 2; r <= 1000; r++) {
              ws.getCell(`G${r}`).dataValidation = {
                type: "list", allowBlank: true, formulae: [`"${inline}"`],
                showErrorMessage: true,
                errorTitle: "Invalid Bank",
                error: "Pick a bank from the dropdown — only banks linked to this fund are allowed.",
                promptTitle: "Bank",
                prompt: "Select a bank account code linked to this fund.",
              };
            }
          } else {
            const lookup = wb.addWorksheet("_banks");
            bankCodes.forEach((c, i) => { lookup.getCell(`A${i + 1}`).value = c; });
            lookup.state = "hidden";
            const ref = `_banks!$A$1:$A$${bankCodes.length}`;
            for (let r = 2; r <= 1000; r++) {
              ws.getCell(`G${r}`).dataValidation = {
                type: "list", allowBlank: true, formulae: [ref],
                showErrorMessage: true,
                errorTitle: "Invalid Bank",
                error: "Pick a bank from the dropdown — only banks linked to this fund are allowed.",
                promptTitle: "Bank",
                prompt: "Select a bank account code linked to this fund.",
              };
            }
          }
        }
        const buf = await wb.xlsx.writeBuffer();
        const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = "capital_transactions_template.xlsx"; a.click();
        URL.revokeObjectURL(url);
        if (bankCodes.length === 0) {
          toast.warning("Template generated — but no banks are linked to this fund yet, so the Bank dropdown will be empty.");
        }
      } catch (err) {
        toast.error("Could not generate XLSX template: " + (err.message || "unknown error"));
      }
      return;
    }
    // Performance template — unchanged CSV.
    const csv = "Investor Name,Total Income,Management Fee,Incentive Fee,Notes\nJohn Doe,5000,1000,500,Q1 performance\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${type}_transactions_template.csv`; a.click();
  };

  const filteredCapital = capitalTxns.filter(t => filterInvestor !== "all" ? t.investor_id === filterInvestor : true);
  const filteredPerf = perfTxns.filter(t => {
    if (filterInvestor !== "all" && t.investor_id !== filterInvestor) return false;
    if (filterPeriod !== "all" && t.period_id !== filterPeriod) return false;
    return true;
  });

  const { sorted: sortedCapital, sortKey: capSK, sortDir: capSD, toggleSort: toggleCapS } = useSortableTable(filteredCapital);
  const { sorted: sortedPerf, sortKey: perfSK, sortDir: perfSD, toggleSort: togglePerfS } = useSortableTable(filteredPerf);

  const [capPage, setCapPage] = useState(1);
  const [perfPage, setPerfPage] = useState(1);
  const [capPageSize, setCapPageSize] = useState(50);
  const [perfPageSize, setPerfPageSize] = useState(50);

  const { paginatedItems: pagedCapital, totalPages: capTotalPages, safePage: capSafePage } = paginate(sortedCapital, capPage, capPageSize);
  const { paginatedItems: pagedPerf, totalPages: perfTotalPages, safePage: perfSafePage } = paginate(sortedPerf, perfPage, perfPageSize);

  const capSub = filteredCapital.reduce((s, t) => s + (t.subscription_amount || 0), 0);
  const capRed = filteredCapital.reduce((s, t) => s + (t.redemption_amount || 0), 0);
  const perfIncome = filteredPerf.reduce((s, t) => s + (t.total_income || 0), 0);
  const perfFees = filteredPerf.reduce((s, t) => s + (t.management_fee || 0) + (t.incentive_fee || 0), 0);

  const fmt = (v) => `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-5 animate-fade-in" data-testid="transactions-page">
      {/* Header row: Title + Fund selector inline */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Transactions</h1>
          {selectedFund && (
            <div className="flex items-center gap-2 mt-1.5">
              {(user?.role === "super_admin" || user?.role === "admin") && funds.length > 1 ? (
                <Select value={selectedFund.id} onValueChange={(v) => setSelectedFund(funds.find(f => f.id === v))}>
                  <SelectTrigger className="bg-transparent border-none h-auto p-0 text-muted-foreground text-sm font-medium gap-1 hover:text-foreground transition-colors [&>svg]:w-3.5 [&>svg]:h-3.5" data-testid="fund-selector">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {funds.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <span className="text-muted-foreground text-sm">{selectedFund.name}</span>
              )}
              <Badge variant="outline" className="text-[9px] uppercase tracking-widest font-semibold px-2 py-0">{selectedFund.reporting_frequency}</Badge>
            </div>
          )}
        </div>
        {/* Compact summary pills */}
        {selectedFund && (
          <div className="flex items-center gap-4 text-sm">
            {activeTab === "capital" ? (
              <>
                <div className="flex items-center gap-1.5 text-muted-foreground"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" /> Subscriptions <span className="text-foreground font-semibold">{fmt(capSub)}</span></div>
                <div className="flex items-center gap-1.5 text-muted-foreground"><span className="w-1.5 h-1.5 rounded-full bg-red-400 inline-block" /> Redemptions <span className="text-foreground font-semibold">{fmt(capRed)}</span></div>
                <div className="flex items-center gap-1.5 text-muted-foreground"><span className="w-1.5 h-1.5 rounded-full bg-blue-400 inline-block" /> Net <span className="text-foreground font-semibold">{fmt(capSub - capRed)}</span></div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-1.5 text-muted-foreground"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" /> Income <span className="text-foreground font-semibold">{fmt(perfIncome)}</span></div>
                <div className="flex items-center gap-1.5 text-muted-foreground"><span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block" /> Fees <span className="text-foreground font-semibold">{fmt(perfFees)}</span></div>
                <div className="flex items-center gap-1.5 text-muted-foreground"><span className="w-1.5 h-1.5 rounded-full bg-violet-400 inline-block" /> Periods <span className="text-foreground font-semibold">{periods.filter(p => p.has_data).length}/{periods.length}</span></div>
              </>
            )}
          </div>
        )}
      </div>

      {selectedFund && (
        <>
          {/* Toolbar: Tab toggle + Filters + Actions — single row */}
          <div className="flex items-center justify-between gap-3 border-b border-border pb-3">
            <div className="flex items-center gap-3">
              {/* Tab toggle */}
              <div className="flex bg-secondary/50 rounded-lg p-0.5 border border-border" data-testid="txn-tabs">
                <button
                  onClick={() => setActiveTab("capital")}
                  className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-md text-xs font-semibold uppercase tracking-wider transition-all ${activeTab === "capital" ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                  data-testid="tab-capital"
                >
                  <DollarSign className="w-3.5 h-3.5" /> Capital
                </button>
                <button
                  onClick={() => setActiveTab("performance")}
                  className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-md text-xs font-semibold uppercase tracking-wider transition-all ${activeTab === "performance" ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                  data-testid="tab-performance"
                >
                  <TrendingUp className="w-3.5 h-3.5" /> Performance
                </button>
              </div>

              {/* Filters */}
              <Select value={filterInvestor} onValueChange={setFilterInvestor}>
                <SelectTrigger className="bg-secondary/40 border-border/50 h-8 text-xs w-44" data-testid="filter-investor"><SelectValue placeholder="All Investors" /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="all">All Investors</SelectItem>
                  {investors.map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {activeTab === "performance" && (
                <Select value={filterPeriod} onValueChange={setFilterPeriod}>
                  <SelectTrigger className="bg-secondary/40 border-border/50 h-8 text-xs w-36" data-testid="filter-period"><SelectValue placeholder="All Periods" /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="all">All Periods</SelectItem>
                    {periods.map(p => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>

            {canManage && (
              <div className="flex items-center gap-2">
                <Button variant="secondary" size="sm" className="h-8 text-xs border border-border" onClick={() => activeTab === "capital" ? setBulkCapitalDialog(true) : setBulkPerfDialog(true)} data-testid="bulk-upload-btn">
                  <Upload className="w-3.5 h-3.5 mr-1.5" /> CSV Upload
                </Button>
                <Button size="sm" className="h-8 text-xs" onClick={() => activeTab === "capital" ? setCapitalDialog(true) : setPerfDialog(true)} data-testid="add-txn-btn">
                  <Plus className="w-3.5 h-3.5 mr-1.5" /> {activeTab === "capital" ? "Add Transaction" : "Add Entry"}
                </Button>
              </div>
            )}
          </div>

          {/* Table */}
          <Card className="overflow-hidden" data-testid={`${activeTab}-txns-table`}>
            <CardContent className="p-0">
              {activeTab === "capital" ? (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-secondary/20 hover:bg-secondary/20 border-b border-border/50">
                      <SortableHead sortKey="date" currentKey={capSK} sortDir={capSD} onSort={toggleCapS} className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold pl-5 w-28">Date</SortableHead>
                      <SortableHead sortKey="investor_name" currentKey={capSK} sortDir={capSD} onSort={toggleCapS} className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Investor</SortableHead>
                      <SortableHead sortKey="subscription_amount" currentKey={capSK} sortDir={capSD} onSort={toggleCapS} className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold text-right">Subscription</SortableHead>
                      <SortableHead sortKey="redemption_amount" currentKey={capSK} sortDir={capSD} onSort={toggleCapS} className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold text-right">Redemption</SortableHead>
                      <SortableHead sortKey="class_description" currentKey={capSK} sortDir={capSD} onSort={toggleCapS} className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Offering</SortableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Bank</TableHead>
                      {selectedFund?.shares_applicable && <SortableHead sortKey="shares" currentKey={capSK} sortDir={capSD} onSort={toggleCapS} className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold text-right">Shares</SortableHead>}
                      <TableHead className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Notes</TableHead>
                      {canManage && <TableHead className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold w-16 pr-5" />}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {txnLoading ? [1,2,3].map(i => <TableRow key={i}><TableCell colSpan={canManage?(selectedFund?.shares_applicable?9:8):(selectedFund?.shares_applicable?8:7)} className="pl-5"><div className="h-8 bg-secondary/30 rounded animate-pulse" /></TableCell></TableRow>) :
                    sortedCapital.length === 0 ? (
                      <TableRow><TableCell colSpan={canManage?(selectedFund?.shares_applicable?9:8):(selectedFund?.shares_applicable?8:7)} className="text-center py-16 text-muted-foreground text-sm">No capital transactions</TableCell></TableRow>
                    ) : pagedCapital.map(t => (
                      <TableRow key={t.id} className="group" data-testid={`cap-txn-${t.id}`}>
                        <TableCell className="pl-5 text-sm tabular-nums">{t.date}</TableCell>
                        <TableCell className="text-sm">{t.investor_name}</TableCell>
                        <TableCell className="text-right text-sm tabular-nums">{t.subscription_amount > 0 ? <span className="text-emerald-600 dark:text-emerald-400">{fmt(t.subscription_amount)}</span> : <span className="text-muted-foreground/40">—</span>}</TableCell>
                        <TableCell className="text-right text-sm tabular-nums">{t.redemption_amount > 0 ? <span className="text-red-600 dark:text-red-400">{fmt(t.redemption_amount)}</span> : <span className="text-muted-foreground/40">—</span>}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{t.class_description || "—"}</TableCell>
                        <TableCell className="text-sm text-muted-foreground" data-testid={`cap-bank-${t.id}`}>
                          {(() => {
                            const code = (t.bank_account_code || "").trim();
                            if (!code) return <span className="text-muted-foreground/40">—</span>;
                            // Resolve to the friendlier label the manual modal shows
                            // (same source as the dropdown). Fall back to the raw code
                            // if the bank list isn't loaded for this fund yet.
                            const match = banks.find(b => b.bank_account_code === code);
                            return match?.label || code;
                          })()}
                        </TableCell>
                        {selectedFund?.shares_applicable && <TableCell className="text-right text-sm tabular-nums text-muted-foreground">{t.shares ? t.shares.toLocaleString(undefined, {maximumFractionDigits: 4}) : "—"}</TableCell>}
                        <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">{t.notes || "—"}</TableCell>
                        {canManage && <TableCell className="pr-5">
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="icon" className="w-7 h-7 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-blue-400" onClick={() => { setEditCapForm({ ...t }); setEditCapDialog(true); }} data-testid={`edit-cap-${t.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                            <Button variant="ghost" size="icon" className="w-7 h-7 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-red-400" onClick={() => setConfirmDeleteTxn({ id: t.id, type: "capital" })} data-testid={`del-cap-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                          </div>
                        </TableCell>}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-secondary/20 hover:bg-secondary/20 border-b border-border/50">
                      <SortableHead sortKey="period_id" currentKey={perfSK} sortDir={perfSD} onSort={togglePerfS} className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold pl-5 w-32">Period</SortableHead>
                      <SortableHead sortKey="investor_name" currentKey={perfSK} sortDir={perfSD} onSort={togglePerfS} className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Investor</SortableHead>
                      <SortableHead sortKey="class_description" currentKey={perfSK} sortDir={perfSD} onSort={togglePerfS} className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Offering</SortableHead>
                      <SortableHead sortKey="total_income" currentKey={perfSK} sortDir={perfSD} onSort={togglePerfS} className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold text-right">Gross Income</SortableHead>
                      <SortableHead sortKey="management_fee" currentKey={perfSK} sortDir={perfSD} onSort={togglePerfS} className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold text-right">Mgmt Fee</SortableHead>
                      <SortableHead sortKey="incentive_fee" currentKey={perfSK} sortDir={perfSD} onSort={togglePerfS} className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold text-right">Incentive Fee</SortableHead>
                      {canManage && <TableHead className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold w-16 pr-5" />}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {txnLoading ? [1,2,3].map(i => <TableRow key={i}><TableCell colSpan={canManage?7:6} className="pl-5"><div className="h-8 bg-secondary/30 rounded animate-pulse" /></TableCell></TableRow>) :
                    sortedPerf.length === 0 ? (
                      <TableRow><TableCell colSpan={canManage?7:6} className="text-center py-16 text-muted-foreground text-sm">No performance transactions</TableCell></TableRow>
                    ) : pagedPerf.map(t => (
                      <TableRow key={t.id} className="group" data-testid={`perf-txn-${t.id}`}>
                        <TableCell className="pl-5 text-sm font-medium">{periods.find(p => p.id === t.period_id)?.label || t.period_id}</TableCell>
                        <TableCell className="text-sm">{t.investor_name}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{t.class_description || "—"}</TableCell>
                        <TableCell className="text-right text-sm tabular-nums text-emerald-600 dark:text-emerald-400">{fmt(t.total_income || 0)}</TableCell>
                        <TableCell className="text-right text-sm tabular-nums text-amber-600 dark:text-amber-400">{fmt(t.management_fee || 0)}</TableCell>
                        <TableCell className="text-right text-sm tabular-nums text-violet-600 dark:text-violet-400">{fmt(t.incentive_fee || 0)}</TableCell>
                        {canManage && <TableCell className="pr-5">
                          {(() => {
                            const status = t.status || "prepared";
                            const isEditable = status === "prepared" || status === "rejected";
                            return (
                              <div className="flex items-center gap-1" title={isEditable ? "" : `Edit locked — statement is ${status.replace(/_/g, " ")}`}>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="w-7 h-7 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-blue-400 disabled:opacity-30 disabled:cursor-not-allowed"
                                  disabled={!isEditable}
                                  onClick={() => { if (isEditable) { setEditPerfForm({ ...t }); setEditPerfDialog(true); } }}
                                  data-testid={`edit-perf-${t.id}`}
                                >
                                  <Pencil className="w-3.5 h-3.5" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="w-7 h-7 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-red-400 disabled:opacity-30 disabled:cursor-not-allowed"
                                  disabled={!isEditable}
                                  onClick={() => { if (isEditable) setConfirmDeleteTxn({ id: t.id, type: "performance" }); }}
                                  data-testid={`del-perf-${t.id}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            );
                          })()}
                        </TableCell>}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Pagination */}
          {activeTab === "capital" ? (
            <Pagination currentPage={capSafePage} totalPages={capTotalPages} onPageChange={setCapPage} pageSize={capPageSize} onPageSizeChange={(v) => { setCapPageSize(v); setCapPage(1); }} />
          ) : (
            <Pagination currentPage={perfSafePage} totalPages={perfTotalPages} onPageChange={setPerfPage} pageSize={perfPageSize} onPageSizeChange={(v) => { setPerfPageSize(v); setPerfPage(1); }} />
          )}

          {/* Row count */}
          <p className="text-xs text-muted-foreground">{activeTab === "capital" ? sortedCapital.length : sortedPerf.length} {activeTab === "capital" ? "transaction" : "entr"}{(activeTab === "capital" ? sortedCapital.length : sortedPerf.length) === 1 ? (activeTab === "capital" ? "" : "y") : (activeTab === "capital" ? "s" : "ies")}</p>
        </>
      )}

      {!selectedFund && !loading && (
        <div className="flex flex-col items-center justify-center h-64 text-muted-foreground text-sm">No fund available</div>
      )}

      {/* Add Capital Transaction Dialog */}
      <Dialog open={capitalDialog} onOpenChange={setCapitalDialog}>
        <DialogContent className="bg-card border-border sm:max-w-md" data-testid="capital-txn-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Capital Transaction</DialogTitle>
            <DialogDescription>Record a subscription or redemption</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Investor</Label>
              <Select value={capForm.investor_id} onValueChange={(v) => setCapForm({...capForm, investor_id: v})}>
                <SelectTrigger className="bg-secondary/50" data-testid="cap-investor-select"><SelectValue placeholder="Select investor" /></SelectTrigger>
                <SelectContent className="bg-card border-border">{investors.map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Date</Label>
              <Input type="date" value={capForm.date} onChange={e => setCapForm({...capForm, date: e.target.value})} className="bg-secondary/50" data-testid="cap-date-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Dealing Date <span className="text-muted-foreground/70">(optional)</span></Label>
              <Input type="date" value={capForm.dealing_date} onChange={e => setCapForm({...capForm, dealing_date: e.target.value})} className="bg-secondary/50" data-testid="cap-dealing-date-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Subscription</Label>
                <Input type="number" step="0.01" placeholder="0.00" value={capForm.subscription_amount} onChange={e => setCapForm({...capForm, subscription_amount: e.target.value})} className="bg-secondary/50" data-testid="cap-sub-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Redemption</Label>
                <Input type="number" step="0.01" placeholder="0.00" value={capForm.redemption_amount} onChange={e => setCapForm({...capForm, redemption_amount: e.target.value})} className="bg-secondary/50" data-testid="cap-red-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Bank</Label>
              <Select
                value={capForm.bank_account_code}
                onValueChange={(v) => {
                  const b = banks.find(x => x.bank_account_code === v);
                  setCapForm({ ...capForm, bank_account_code: v, bank_id: b?.bank_id || "" });
                }}
                disabled={banksLoading || banks.length === 0}
              >
                <SelectTrigger className="bg-secondary/50" data-testid="cap-bank-select">
                  <SelectValue placeholder={
                    banksLoading
                      ? "Loading banks…"
                      : banksError
                        ? banksError
                        : banks.length === 0
                          ? "No banks available for this fund"
                          : "Select bank"
                  } />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {banks.map(b => (
                    <SelectItem key={b.bank_account_code} value={b.bank_account_code}>
                      {b.label || `${b.bank_account_code}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {(() => {
              const selInv = investors.find(i => i.id === capForm.investor_id);
              const invOfferings = selInv?.classes || [];
              return invOfferings.length > 0 ? (
                <div className="space-y-1.5">
                  <Label className="text-xs">Offering</Label>
                  <Select value={capForm.class_description} onValueChange={(v) => setCapForm({...capForm, class_description: v})}>
                    <SelectTrigger className="bg-secondary/50" data-testid="cap-class-select"><SelectValue placeholder="Select offering" /></SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {invOfferings.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ) : null;
            })()}
            {selectedFund?.shares_applicable && (
              <div className="space-y-1.5">
                <Label className="text-xs">Shares / Units</Label>
                <Input type="number" step="0.0001" placeholder="0" value={capForm.shares} onChange={e => setCapForm({...capForm, shares: e.target.value})} className="bg-secondary/50" data-testid="cap-shares-input" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs">Notes</Label>
              <Input value={capForm.notes} onChange={e => setCapForm({...capForm, notes: e.target.value})} className="bg-secondary/50" placeholder="Optional" data-testid="cap-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={() => setCapitalDialog(false)}>Cancel</Button>
            <Button size="sm" onClick={createCapitalTxn} disabled={submitting} data-testid="save-cap-txn-btn">{submitting ? "Saving..." : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Performance Entry Dialog */}
      <Dialog open={perfDialog} onOpenChange={setPerfDialog}>
        <DialogContent className="bg-card border-border sm:max-w-md" data-testid="perf-txn-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Performance Entry</DialogTitle>
            <DialogDescription>Income and fees for a reporting period</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Investor</Label>
                <Select value={perfForm.investor_id} onValueChange={(v) => setPerfForm({...perfForm, investor_id: v, class_description: ""})}>
                  <SelectTrigger className="bg-secondary/50" data-testid="perf-investor-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="bg-card border-border">{investors.map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Period (Auto-computed)</Label>
                <div className="flex items-center h-9 px-3 rounded-md border border-border bg-secondary/30 text-sm" data-testid="perf-period-display">
                  {nextPeriodLoading ? (
                    <span className="text-muted-foreground">Computing...</span>
                  ) : perfForm.period_id ? (
                    <span className="font-medium">{nextPeriodLabel}</span>
                  ) : perfForm.investor_id ? (
                    <span className="text-muted-foreground text-xs">{nextPeriodLabel || "Select investor first"}</span>
                  ) : (
                    <span className="text-muted-foreground">Select investor first</span>
                  )}
                </div>
              </div>
            </div>
            {(() => {
              const selInv = investors.find(i => i.id === perfForm.investor_id);
              const invOfferings = selInv?.classes || [];
              return invOfferings.length > 0 ? (
                <div className="space-y-1.5">
                  <Label className="text-xs">Offering</Label>
                  <Select value={perfForm.class_description} onValueChange={(v) => setPerfForm({...perfForm, class_description: v})}>
                    <SelectTrigger className="bg-secondary/50" data-testid="perf-offering-select"><SelectValue placeholder="Select offering" /></SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {invOfferings.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ) : null;
            })()}
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Gross Income</Label>
                <Input type="number" step="0.01" placeholder="0.00" value={perfForm.total_income} onChange={e => setPerfForm({...perfForm, total_income: e.target.value})} className="bg-secondary/50" data-testid="perf-income-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Mgmt Fee</Label>
                <Input type="number" step="0.01" placeholder="0.00" value={perfForm.management_fee} onChange={e => setPerfForm({...perfForm, management_fee: e.target.value})} className="bg-secondary/50" data-testid="perf-mgmt-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Incentive Fee</Label>
                <Input type="number" step="0.01" placeholder="0.00" value={perfForm.incentive_fee} onChange={e => setPerfForm({...perfForm, incentive_fee: e.target.value})} className="bg-secondary/50" data-testid="perf-incentive-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Notes</Label>
              <Input value={perfForm.notes} onChange={e => setPerfForm({...perfForm, notes: e.target.value})} className="bg-secondary/50" placeholder="Optional" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={() => setPerfDialog(false)}>Cancel</Button>
            <Button size="sm" onClick={createPerfTxn} disabled={submitting} data-testid="save-perf-txn-btn">{submitting ? "Saving..." : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Capital Upload */}
      <Dialog open={bulkCapitalDialog} onOpenChange={(v) => { setBulkCapitalDialog(v); if (!v) { setBulkFile(null); setBulkResult(null); } }}>
        <DialogContent className="bg-card border-border sm:max-w-lg" data-testid="bulk-capital-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Bulk Upload — Capital</DialogTitle>
            <DialogDescription>CSV: Date, Investor Name, Subscription Amount, Redemption Amount, Shares, Class, Notes</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={() => downloadTemplate("capital")} data-testid="download-cap-template">
              <Download className="w-3 h-3 mr-1" /> Download CSV template
            </Button>
            <div className="border border-dashed border-border/60 rounded-lg p-5 text-center cursor-pointer hover:border-primary/40 transition-colors" onClick={() => bulkCapRef.current?.click()}>
              <input ref={bulkCapRef} type="file" accept=".csv,.xlsx" className="hidden" onChange={e => { setBulkFile(e.target.files?.[0]); setBulkResult(null); }} />
              <p className="text-xs text-muted-foreground">{bulkFile ? bulkFile.name : "Click to select CSV"}</p>
            </div>
            {bulkResult && bulkResult.errors.length > 0 && (
              <BulkUploadResultPanel result={bulkResult} testIdPrefix="bulk-cap" />
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={() => { setBulkCapitalDialog(false); setBulkFile(null); setBulkResult(null); }}>
              {bulkResult ? "Close" : "Cancel"}
            </Button>
            <Button size="sm" onClick={handleBulkCapitalUpload} disabled={submitting || !bulkFile} data-testid="upload-cap-csv-btn">{submitting ? "Uploading..." : (bulkResult ? "Re-upload" : "Upload")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Performance Upload */}
      <Dialog open={bulkPerfDialog} onOpenChange={(v) => { setBulkPerfDialog(v); if (!v) { setBulkFile(null); setBulkPeriod(""); setBulkResult(null); } }}>
        <DialogContent className="bg-card border-border sm:max-w-lg" data-testid="bulk-perf-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Bulk Upload — Performance</DialogTitle>
            <DialogDescription>CSV: Investor Name, Gross Income, Management Fee, Incentive Fee, Notes</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Reporting Period</Label>
              <Select value={bulkPeriod} onValueChange={setBulkPeriod}>
                <SelectTrigger className="bg-secondary/50" data-testid="bulk-period-select"><SelectValue placeholder="Select period" /></SelectTrigger>
                <SelectContent className="bg-card border-border">{periods.map(p => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={() => downloadTemplate("performance")} data-testid="download-perf-template">
              <Download className="w-3 h-3 mr-1" /> Download CSV template
            </Button>
            <div className="border border-dashed border-border/60 rounded-lg p-5 text-center cursor-pointer hover:border-primary/40 transition-colors" onClick={() => bulkPerfRef.current?.click()}>
              <input ref={bulkPerfRef} type="file" accept=".csv" className="hidden" onChange={e => { setBulkFile(e.target.files?.[0]); setBulkResult(null); }} />
              <p className="text-xs text-muted-foreground">{bulkFile ? bulkFile.name : "Click to select CSV"}</p>
            </div>
            {bulkResult && bulkResult.errors.length > 0 && (
              <BulkUploadResultPanel result={bulkResult} testIdPrefix="bulk-perf" />
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={() => { setBulkPerfDialog(false); setBulkFile(null); setBulkPeriod(""); setBulkResult(null); }}>
              {bulkResult ? "Close" : "Cancel"}
            </Button>
            <Button size="sm" onClick={handleBulkPerfUpload} disabled={submitting || !bulkFile || !bulkPeriod} data-testid="upload-perf-csv-btn">{submitting ? "Uploading..." : (bulkResult ? "Re-upload" : "Upload")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={!!confirmDeleteTxn}
        onOpenChange={(v) => !v && setConfirmDeleteTxn(null)}
        title="Delete Transaction"
        description="Are you sure you want to delete this transaction? This action cannot be undone."
        confirmLabel="Delete"
        onConfirm={() => {
          if (confirmDeleteTxn?.type === "capital") deleteCapitalTxn(confirmDeleteTxn.id);
          else deletePerfTxn(confirmDeleteTxn.id);
        }}
      />

      {/* Edit Capital Transaction Dialog */}
      <Dialog open={editCapDialog} onOpenChange={(open) => { if (!open) { setEditCapDialog(false); setEditCapForm(null); } }}>
        <DialogContent className="bg-card border-border sm:max-w-md" data-testid="edit-capital-txn-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Capital Transaction</DialogTitle>
            <DialogDescription>Update the transaction details</DialogDescription>
          </DialogHeader>
          {editCapForm && (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Investor</Label>
                <div className="flex items-center h-9 px-3 rounded-md border border-border bg-secondary/30 text-sm">
                  <span>{editCapForm.investor_name}</span>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Date</Label>
                <Input type="date" value={editCapForm.date || ""} onChange={e => setEditCapForm({...editCapForm, date: e.target.value})} className="bg-secondary/50" data-testid="edit-cap-date-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Dealing Date <span className="text-muted-foreground/70">(optional)</span></Label>
                <Input type="date" value={editCapForm.dealing_date || ""} onChange={e => setEditCapForm({...editCapForm, dealing_date: e.target.value})} className="bg-secondary/50" data-testid="edit-cap-dealing-date-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Subscription</Label>
                  <Input type="number" step="0.01" value={editCapForm.subscription_amount || ""} onChange={e => setEditCapForm({...editCapForm, subscription_amount: e.target.value})} className="bg-secondary/50" data-testid="edit-cap-sub-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Redemption</Label>
                  <Input type="number" step="0.01" value={editCapForm.redemption_amount || ""} onChange={e => setEditCapForm({...editCapForm, redemption_amount: e.target.value})} className="bg-secondary/50" data-testid="edit-cap-red-input" />
                </div>
              </div>
              {selectedFund?.shares_applicable && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Shares / Units</Label>
                  <Input type="number" step="0.0001" value={editCapForm.shares || ""} onChange={e => setEditCapForm({...editCapForm, shares: e.target.value})} className="bg-secondary/50" data-testid="edit-cap-shares-input" />
                </div>
              )}
              <div className="space-y-1.5">
                <Label className="text-xs">Notes</Label>
                <Input value={editCapForm.notes || ""} onChange={e => setEditCapForm({...editCapForm, notes: e.target.value})} className="bg-secondary/50" data-testid="edit-cap-notes-input" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={() => { setEditCapDialog(false); setEditCapForm(null); }}>Cancel</Button>
            <Button size="sm" onClick={editCapitalTxn} disabled={submitting} data-testid="save-edit-cap-btn">{submitting ? "Saving..." : "Update"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Performance Entry Dialog */}
      <Dialog open={editPerfDialog} onOpenChange={(open) => { if (!open) { setEditPerfDialog(false); setEditPerfForm(null); } }}>
        <DialogContent className="bg-card border-border sm:max-w-md" data-testid="edit-perf-txn-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Performance Entry</DialogTitle>
            <DialogDescription>Update income and fees</DialogDescription>
          </DialogHeader>
          {editPerfForm && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Investor</Label>
                  <div className="flex items-center h-9 px-3 rounded-md border border-border bg-secondary/30 text-sm">
                    <span>{editPerfForm.investor_name}</span>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Period</Label>
                  <div className="flex items-center h-9 px-3 rounded-md border border-border bg-secondary/30 text-sm">
                    <span>{periods.find(p => p.id === editPerfForm.period_id)?.label || editPerfForm.period_id}</span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Gross Income</Label>
                  <Input type="number" step="0.01" value={editPerfForm.total_income || ""} onChange={e => setEditPerfForm({...editPerfForm, total_income: e.target.value})} className="bg-secondary/50" data-testid="edit-perf-income-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Mgmt Fee</Label>
                  <Input type="number" step="0.01" value={editPerfForm.management_fee || ""} onChange={e => setEditPerfForm({...editPerfForm, management_fee: e.target.value})} className="bg-secondary/50" data-testid="edit-perf-mgmt-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Incentive Fee</Label>
                  <Input type="number" step="0.01" value={editPerfForm.incentive_fee || ""} onChange={e => setEditPerfForm({...editPerfForm, incentive_fee: e.target.value})} className="bg-secondary/50" data-testid="edit-perf-incentive-input" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Notes</Label>
                <Input value={editPerfForm.notes || ""} onChange={e => setEditPerfForm({...editPerfForm, notes: e.target.value})} className="bg-secondary/50" data-testid="edit-perf-notes-input" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={() => { setEditPerfDialog(false); setEditPerfForm(null); }}>Cancel</Button>
            <Button size="sm" onClick={editPerfTxn} disabled={submitting} data-testid="save-edit-perf-btn">{submitting ? "Saving..." : "Update"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
